import { dayjs } from '@tunasong/models'
import type { Dayjs } from 'dayjs'
import type { DateSlot, Poll } from '@tunasong/schemas'
import { isDateSlot } from '@tunasong/schemas'

/** A rehearsal with no stated end is assumed to run this long. */
export const defaultRehearsalHours = 2

/** The default hour for the very first slot, when there is no previous slot to copy. */
const defaultRehearsalHour = 19

/**
 * Short, scannable, no year: `Tue 24 Mar, 19:00`.
 *
 * Slots are stored as ISO instants and rendered in the viewer's local zone. A member
 * answering from another timezone sees 19:00 Oslo as 13:00 their time, which is correct:
 * the rehearsal happens at one moment, in one room.
 */
export const formatSlotLabel = (start: string) => dayjs(start).format('ddd D MMM, HH:mm')

export const getSlotEnd = (slot: DateSlot) =>
  slot.end ?? dayjs(slot.start).add(defaultRehearsalHours, 'hour').toISOString()

/** The accessible name and hover title: `Tuesday 24 March 2026, 19:00–21:00`. */
export const formatSlotFullLabel = (slot: DateSlot) =>
  `${dayjs(slot.start).format('dddd D MMMM YYYY, HH:mm')}–${dayjs(getSlotEnd(slot)).format('HH:mm')}`

/** Past means ended, not started — a rehearsal already under way is still joinable. */
export const isPastSlot = (slot: DateSlot, now?: string) => dayjs(getSlotEnd(slot)).isBefore(dayjs(now))

export const getNextSlotStart = (previousStart?: string, now?: string) =>
  previousStart
    ? dayjs(previousStart).add(7, 'day').toISOString()
    : dayjs(now).add(1, 'day').hour(defaultRehearsalHour).minute(0).second(0).millisecond(0).toISOString()

/**
 * Non-tombstoned dated slot indices, chronologically. Tombstones stay in the array so the
 * indices responses are keyed by never shift.
 */
export const getLiveSlotIndices = (poll: Poll) =>
  poll.pollOptions
    .map((option, idx) => ({ option, idx }))
    .filter(({ option }) => !option.removed && isDateSlot(option))
    .sort((a, b) => (a.option.start ?? '').localeCompare(b.option.start ?? ''))
    .map(({ idx }) => idx)

/**
 * Coerce a picked respond-by day to the END of that day.
 *
 * A bare date used as a deadline means the end of it: "Respond by Tuesday" must not make
 * someone late at 00:01 on Tuesday. `DatePicker` hands back midnight, which means exactly the
 * opposite, so the coercion happens here — once, at the write boundary — rather than at each
 * comparison, because a read site that forgets it reintroduces the bug.
 */
export const toRespondByInstant = (day: Dayjs | null) => (day ? day.endOf('day').toISOString() : undefined)

export const isRespondByPassed = (respondBy: string | undefined, now?: string) =>
  respondBy ? dayjs(respondBy).isBefore(dayjs(now)) : false
